import React, { useCallback, useEffect, useState } from 'react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import { MessageSquareQuote, Sparkles, Plus, Pencil, Trash2, GripVertical, AlertTriangle, Loader2 } from 'lucide-react';
import {
    VoiceRule,
    VoiceModuleOption,
    listVoiceRuleModules,
    listVoiceRules,
    createVoiceRule,
    updateVoiceRule,
    deleteVoiceRule,
    reorderVoiceRules,
} from '../../services/api';
import VoiceRuleSuggestModal from './VoiceRuleSuggestModal';

const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
    }
    return fallback;
};

const ALL_SCOPE = 'all';

interface Props {
    projectId: string;
}

/** One rule row: text, scope chips, active toggle, edit + delete buttons.
 * Kept in this file (not a separate component file) per the spec — it is
 * small and used only here. */
const VoiceRuleCard: React.FC<{
    rule: VoiceRule;
    modules: VoiceModuleOption[];
    busy: boolean;
    confirmingDelete: boolean;
    draggable: boolean;
    onToggleActive: () => void;
    onEdit: () => void;
    onRequestDelete: () => void;
    onCancelDelete: () => void;
    onConfirmDelete: () => void;
    onDragStart: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: () => void;
}> = ({
    rule,
    modules,
    busy,
    confirmingDelete,
    draggable,
    onToggleActive,
    onEdit,
    onRequestDelete,
    onCancelDelete,
    onConfirmDelete,
    onDragStart,
    onDragOver,
    onDrop,
}) => {
    const moduleLabel = (key: string): string | null => {
        const found = modules.find((m) => m.key === key);
        return found ? found.label : null;
    };

    return (
        <div
            className="ps-member-row"
            style={{ flexWrap: 'wrap', rowGap: 'var(--space-2)' }}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
        >
            <GripVertical size={16} style={{ color: 'var(--text-secondary)', cursor: 'grab', flexShrink: 0 }} />
            <div className="ps-member-info">
                <span className="ps-vt-rule-text">{rule.rule_text}</span>
                <div className="ps-vt-chips">
                    {rule.scope.includes(ALL_SCOPE) ? (
                        <span className="ps-vt-chip ps-vt-chip--all">
                            <GlassBadge variant="default" size="sm">All modules</GlassBadge>
                        </span>
                    ) : (
                        rule.scope.map((key) => {
                            const label = moduleLabel(key);
                            return (
                                <span key={key} className={`ps-vt-chip ${label ? '' : 'ps-vt-chip--unknown'}`}>
                                    <GlassBadge variant={label ? 'info' : 'default'} size="sm">
                                        {label || `Unknown scope: ${key}`}
                                    </GlassBadge>
                                </span>
                            );
                        })
                    )}
                    {rule.source === 'ai_suggested' && (
                        <span className="ps-vt-chip">
                            <GlassBadge variant="success" size="sm">AI suggested</GlassBadge>
                        </span>
                    )}
                    {!rule.active && (
                        <span className="ps-vt-chip">
                            <GlassBadge variant="warning" size="sm">Inactive</GlassBadge>
                        </span>
                    )}
                </div>
            </div>
            <div className="ps-vt-rule-actions">
                <label className="ps-toggle-switch" title={rule.active ? 'Active' : 'Inactive'}>
                    <input
                        type="checkbox"
                        checked={rule.active}
                        disabled={busy}
                        onChange={onToggleActive}
                    />
                    <span className="ps-toggle-slider"></span>
                </label>
                {confirmingDelete ? (
                    <>
                        <GlassButton variant="danger" size="sm" loading={busy} disabled={busy} onClick={onConfirmDelete}>
                            Delete rule
                        </GlassButton>
                        <GlassButton variant="secondary" size="sm" disabled={busy} onClick={onCancelDelete}>
                            Cancel
                        </GlassButton>
                    </>
                ) : (
                    <>
                        <GlassButton variant="secondary" size="sm" icon={<Pencil size={14} />} onClick={onEdit} title="Edit rule" />
                        <GlassButton variant="secondary" size="sm" icon={<Trash2 size={14} />} onClick={onRequestDelete} title="Delete rule" />
                    </>
                )}
            </div>
        </div>
    );
};

const VoiceToneSection: React.FC<Props> = ({ projectId }) => {
    const [rules, setRules] = useState<VoiceRule[]>([]);
    const [modules, setModules] = useState<VoiceModuleOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Inline add/edit editor. 'new' = add mode; a rule_id = editing that rule.
    const [editingId, setEditingId] = useState<string | 'new' | null>(null);
    const [draftText, setDraftText] = useState('');
    const [draftScope, setDraftScope] = useState<string[]>([ALL_SCOPE]);
    const [savingDraft, setSavingDraft] = useState(false);
    const [draftError, setDraftError] = useState<string | null>(null);

    const [busyRuleId, setBusyRuleId] = useState<string | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [dragId, setDragId] = useState<string | null>(null);
    const [showSuggest, setShowSuggest] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [rulesData, modulesData] = await Promise.all([
                listVoiceRules(projectId),
                listVoiceRuleModules(),
            ]);
            setRules(rulesData);
            setModules(modulesData.modules);
        } catch (err) {
            setError(getApiError(err, 'Failed to load voice & tone rules'));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) load();
    }, [projectId, load]);

    const openAddEditor = () => {
        setEditingId('new');
        setDraftText('');
        setDraftScope([ALL_SCOPE]);
        setDraftError(null);
    };

    const openEditEditor = (rule: VoiceRule) => {
        setEditingId(rule.rule_id);
        setDraftText(rule.rule_text);
        setDraftScope(rule.scope.length ? rule.scope : [ALL_SCOPE]);
        setDraftError(null);
    };

    const cancelEditor = () => {
        setEditingId(null);
        setDraftText('');
        setDraftScope([ALL_SCOPE]);
        setDraftError(null);
    };

    const toggleDraftScope = (key: string) => {
        setDraftScope((prev) => {
            if (key === ALL_SCOPE) return [ALL_SCOPE];
            const withoutAll = prev.filter((s) => s !== ALL_SCOPE);
            if (withoutAll.includes(key)) {
                const next = withoutAll.filter((s) => s !== key);
                return next.length ? next : [ALL_SCOPE];
            }
            return [...withoutAll, key];
        });
    };

    const saveDraft = async () => {
        const text = draftText.trim();
        if (!text) {
            setDraftError('Rule text is required');
            return;
        }
        setSavingDraft(true);
        setDraftError(null);
        try {
            if (editingId === 'new') {
                const created = await createVoiceRule(projectId, {
                    rule_text: text,
                    scope: draftScope,
                    source: 'manual',
                });
                setRules((prev) => [...prev, created]);
            } else if (editingId) {
                const updated = await updateVoiceRule(projectId, editingId, {
                    rule_text: text,
                    scope: draftScope,
                });
                setRules((prev) => prev.map((r) => (r.rule_id === updated.rule_id ? updated : r)));
            }
            cancelEditor();
        } catch (err) {
            setDraftError(getApiError(err, 'Failed to save rule'));
        } finally {
            setSavingDraft(false);
        }
    };

    // Optimistic toggle, revert on error — matches the allow_emojis pattern
    // in ProjectSettings.tsx.
    const toggleActive = async (rule: VoiceRule) => {
        const nextActive = !rule.active;
        setRules((prev) => prev.map((r) => (r.rule_id === rule.rule_id ? { ...r, active: nextActive } : r)));
        setBusyRuleId(rule.rule_id);
        try {
            await updateVoiceRule(projectId, rule.rule_id, { active: nextActive });
        } catch (err) {
            setRules((prev) => prev.map((r) => (r.rule_id === rule.rule_id ? { ...r, active: !nextActive } : r)));
            setError(getApiError(err, 'Failed to update rule'));
        } finally {
            setBusyRuleId(null);
        }
    };

    const handleDelete = async (ruleId: string) => {
        setBusyRuleId(ruleId);
        const prevRules = rules;
        setRules((prev) => prev.filter((r) => r.rule_id !== ruleId));
        try {
            await deleteVoiceRule(projectId, ruleId);
            setConfirmDeleteId(null);
        } catch (err) {
            setRules(prevRules);
            setError(getApiError(err, 'Failed to delete rule'));
        } finally {
            setBusyRuleId(null);
        }
    };

    const handleDrop = async (targetId: string) => {
        const sourceId = dragId;
        setDragId(null);
        if (!sourceId || sourceId === targetId) return;

        const prevRules = rules;
        const ids = rules.map((r) => r.rule_id);
        const from = ids.indexOf(sourceId);
        const to = ids.indexOf(targetId);
        if (from === -1 || to === -1) return;

        const reordered = [...rules];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        setRules(reordered);

        try {
            const saved = await reorderVoiceRules(projectId, reordered.map((r) => r.rule_id));
            setRules(saved);
        } catch (err) {
            setRules(prevRules);
            setError(getApiError(err, 'Failed to reorder rules'));
        }
    };

    const activeCount = rules.filter((r) => r.active).length;

    return (
        <GlassCard padding="lg">
            <div className="ps-section-header">
                <MessageSquareQuote size={20} />
                <h3>Voice &amp; Tone</h3>
                {!loading && (
                    <span className="ps-vt-count">
                        {rules.length} rule{rules.length === 1 ? '' : 's'} · {activeCount} active
                    </span>
                )}
            </div>
            <p className="ps-description">
                Standing behavioural rules AIME follows when generating content for this project — a
                different axis from your Brand Guide's identity (voice, tagline, colours). Example:
                "Never use superlatives like 'best' or '#1'."
            </p>

            {error && (
                <div className="ps-inline-error">
                    <AlertTriangle size={14} />
                    <span>{error}</span>
                </div>
            )}

            {loading ? (
                <div className="ps-loading">
                    <Loader2 size={16} className="ps-spin" />
                    <span>Loading rules...</span>
                </div>
            ) : rules.length === 0 && editingId !== 'new' ? (
                <p className="ps-empty-state">
                    No voice &amp; tone rules yet. Add one manually or ask AI to suggest some.
                </p>
            ) : (
                <div className="ps-members-list">
                    {rules.map((rule) =>
                        editingId === rule.rule_id ? (
                            <div key={rule.rule_id} className="ps-member-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-3)' }}>
                                <VoiceRuleEditor
                                    modules={modules}
                                    draftText={draftText}
                                    draftScope={draftScope}
                                    saving={savingDraft}
                                    error={draftError}
                                    onTextChange={setDraftText}
                                    onToggleScope={toggleDraftScope}
                                    onSave={saveDraft}
                                    onCancel={cancelEditor}
                                />
                            </div>
                        ) : (
                            <VoiceRuleCard
                                key={rule.rule_id}
                                rule={rule}
                                modules={modules}
                                busy={busyRuleId === rule.rule_id}
                                confirmingDelete={confirmDeleteId === rule.rule_id}
                                draggable={editingId === null}
                                onToggleActive={() => toggleActive(rule)}
                                onEdit={() => openEditEditor(rule)}
                                onRequestDelete={() => setConfirmDeleteId(rule.rule_id)}
                                onCancelDelete={() => setConfirmDeleteId(null)}
                                onConfirmDelete={() => handleDelete(rule.rule_id)}
                                onDragStart={() => setDragId(rule.rule_id)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => handleDrop(rule.rule_id)}
                            />
                        ),
                    )}
                    {editingId === 'new' && (
                        <div className="ps-member-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--space-3)' }}>
                            <VoiceRuleEditor
                                modules={modules}
                                draftText={draftText}
                                draftScope={draftScope}
                                saving={savingDraft}
                                error={draftError}
                                onTextChange={setDraftText}
                                onToggleScope={toggleDraftScope}
                                onSave={saveDraft}
                                onCancel={cancelEditor}
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="ps-vt-toolbar">
                {editingId === null && (
                    <>
                        <GlassButton variant="secondary" size="sm" icon={<Plus size={16} />} onClick={openAddEditor}>
                            Add rule
                        </GlassButton>
                        <GlassButton variant="secondary" size="sm" icon={<Sparkles size={16} />} onClick={() => setShowSuggest(true)}>
                            Suggest rules with AI
                        </GlassButton>
                    </>
                )}
            </div>

            <p className="ps-vt-coverage-note">
                Currently applied to: Content (articles &amp; quick wins), Ad Copy &amp; Multi-Channel,
                Email / EDM generation, Campaigns, Scripts, SEO content briefs, Website page field
                generation (value propositions, target audience, FAQs, testimonials, services, quiz
                setup), Site Builder AI site/section generation, pricing-calculator page FAQ
                generation, and Landing Pages (hero, text blocks, features, testimonials, FAQ,
                services, effect overlays). Not yet applied to Reviews — rules scoped to that module
                are saved but don't affect generation there yet. Rules apply at generation time only;
                existing content isn't retroactively changed.
            </p>

            {showSuggest && (
                <VoiceRuleSuggestModal
                    projectId={projectId}
                    onClose={() => setShowSuggest(false)}
                    onAccepted={(rule) => setRules((prev) => [...prev, rule])}
                />
            )}
        </GlassCard>
    );
};

/** Shared inline editor for both "Add rule" and per-rule "Edit". */
const VoiceRuleEditor: React.FC<{
    modules: VoiceModuleOption[];
    draftText: string;
    draftScope: string[];
    saving: boolean;
    error: string | null;
    onTextChange: (v: string) => void;
    onToggleScope: (key: string) => void;
    onSave: () => void;
    onCancel: () => void;
}> = ({ modules, draftText, draftScope, saving, error, onTextChange, onToggleScope, onSave, onCancel }) => (
    <>
        <div className="ps-form-group" style={{ margin: 0 }}>
            <label className="ps-label">Rule text</label>
            <textarea
                className="ps-textarea"
                value={draftText}
                onChange={(e) => onTextChange(e.target.value)}
                placeholder="e.g. Never use superlatives like 'best' or 'guaranteed'."
                rows={2}
                maxLength={300}
            />
        </div>
        <div className="ps-form-group" style={{ margin: 0 }}>
            <label className="ps-label">Applies to</label>
            <div className="ps-vt-chips">
                <button
                    type="button"
                    className="ps-vt-chip"
                    onClick={() => onToggleScope(ALL_SCOPE)}
                >
                    <GlassBadge variant={draftScope.includes(ALL_SCOPE) ? 'info' : 'default'} size="sm">
                        All modules
                    </GlassBadge>
                </button>
                {modules.map((m) => (
                    <button
                        key={m.key}
                        type="button"
                        className="ps-vt-chip"
                        onClick={() => onToggleScope(m.key)}
                    >
                        <GlassBadge variant={draftScope.includes(m.key) ? 'info' : 'default'} size="sm">
                            {m.label}
                        </GlassBadge>
                    </button>
                ))}
            </div>
        </div>
        {error && (
            <div className="ps-inline-error">
                <AlertTriangle size={14} />
                <span>{error}</span>
            </div>
        )}
        <div className="ps-vt-rule-actions" style={{ justifyContent: 'flex-start', marginLeft: 0 }}>
            <GlassButton variant="primary" size="sm" loading={saving} disabled={saving} onClick={onSave}>
                Save rule
            </GlassButton>
            <GlassButton variant="secondary" size="sm" disabled={saving} onClick={onCancel}>
                Cancel
            </GlassButton>
        </div>
    </>
);

export default VoiceToneSection;
